import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Save, ArrowLeft, ExternalLink, Loader2, Upload } from "lucide-react";
import MarkdownEditor from "../../components/MarkdownEditor";
import { adminApi } from "../../lib/adminApi";

const EMPTY = {
  title: "", slug: "", excerpt: "", body: "",
  cover_image_url: "", cover_image_alt: "",
  author_name: "", author_role: "",
  status: "draft", tags: [],
  seo_title: "", seo_description: "",
};

export default function AdminBlogEdit() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const [post, setPost] = useState(EMPTY);
  const [tagInput, setTagInput] = useState("");
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatusMsg] = useState(null);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (isNew) return;
    (async () => {
      const res = await adminApi.getBlog(id);
      if (res.ok) setPost({ ...EMPTY, ...res.data });
      else setError(res.error);
      setLoading(false);
    })();
  }, [id, isNew]);

  const update = (k, v) => setPost((p) => ({ ...p, [k]: v }));

  const save = async (publish = false) => {
    setSaving(true); setError(null); setStatusMsg(null);
    const body = { ...post, status: publish ? "published" : post.status };
    const res = isNew ? await adminApi.createBlog(body) : await adminApi.updateBlog(id, body);
    setSaving(false);
    if (!res.ok) { setError(res.error); return; }
    setStatusMsg(publish ? "Published." : "Saved.");
    if (isNew) navigate(`/admin/blog/${res.data.id}`, { replace: true });
    else setPost({ ...EMPTY, ...res.data });
  };

  const onCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const res = await adminApi.uploadImage(file);
    setUploading(false);
    if (res.ok) update("cover_image_url", res.data.url);
    else alert(res.error);
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    if (!post.tags.includes(t)) update("tags", [...post.tags, t]);
    setTagInput("");
  };

  if (loading) return <p className="p-6 text-sm text-ridge-muted">Loading…</p>;

  return (
    <div data-testid="admin-blog-edit">
      <header className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-end">
        <div>
          <button
            type="button"
            onClick={() => navigate("/admin/blog")}
            className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-ridge-muted hover:text-ridge-charcoal"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to posts
          </button>
          <h1 className="font-display mt-2 text-3xl font-bold tracking-tight text-ridge-charcoal md:text-4xl">
            {isNew ? "New Post" : "Edit Post"}
          </h1>
          {post.slug && !isNew ? (
            <p className="mt-1 text-xs text-ridge-muted">
              Slug: <code className="rounded bg-ridge-stone px-1.5 py-0.5">{post.slug}</code>
              {post.status === "published" ? (
                <a href={`/blog/${post.slug}`} target="_blank" rel="noreferrer" className="ml-2 text-ridge-amber">
                  View live <ExternalLink className="ml-1 inline h-3 w-3" />
                </a>
              ) : null}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => save(false)} disabled={saving} className="btn-secondary px-5 py-2 text-xs" data-testid="admin-blog-save-draft">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save{post.status === "draft" ? " draft" : ""}
          </button>
          <button type="button" onClick={() => save(true)} disabled={saving} className="btn-accent px-5 py-2 text-xs" data-testid="admin-blog-publish">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Publish
          </button>
        </div>
      </header>

      {error ? <p className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">{error}</p> : null}
      {status ? <p className="mt-4 rounded-md border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-700">{status}</p> : null}

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="space-y-5 lg:col-span-8">
          <Field label="Title" required>
            <input type="text" value={post.title} onChange={(e) => update("title", e.target.value)} className={inputCls} data-testid="admin-blog-title" />
          </Field>
          <Field label="Slug" hint="Auto-generated from title if empty.">
            <input type="text" value={post.slug || ""} onChange={(e) => update("slug", e.target.value)} className={inputCls} data-testid="admin-blog-slug" />
          </Field>
          <Field label="Excerpt" hint="~160 characters ideal.">
            <textarea value={post.excerpt || ""} onChange={(e) => update("excerpt", e.target.value)} rows={3} maxLength={400} className={inputCls} data-testid="admin-blog-excerpt" />
          </Field>
          <Field label="Body (markdown)">
            <MarkdownEditor value={post.body} onChange={(v) => update("body", v)} testId="admin-blog-body" />
          </Field>
        </div>

        <aside className="space-y-5 lg:col-span-4">
          <Field label="Status">
            <select value={post.status} onChange={(e) => update("status", e.target.value)} className={inputCls} data-testid="admin-blog-status">
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </Field>
          <Field label="Cover image URL">
            <input type="url" value={post.cover_image_url || ""} onChange={(e) => update("cover_image_url", e.target.value)} placeholder="https://… or /uploads/…" className={inputCls} data-testid="admin-blog-cover-url" />
            <label className="mt-2 inline-flex cursor-pointer items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-ridge-amber hover:text-ridge-amber-dark">
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
              {uploading ? "Uploading…" : "Upload image"}
              <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="sr-only" onChange={onCoverUpload} data-testid="admin-blog-cover-upload" />
            </label>
            {post.cover_image_url ? (
              <img src={post.cover_image_url} alt="" className="mt-3 max-h-40 w-full rounded-md border border-ridge-border object-cover" />
            ) : null}
          </Field>
          <Field label="Cover image alt text">
            <input type="text" value={post.cover_image_alt || ""} onChange={(e) => update("cover_image_alt", e.target.value)} className={inputCls} data-testid="admin-blog-cover-alt" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Author">
              <input type="text" value={post.author_name || ""} onChange={(e) => update("author_name", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Author role">
              <input type="text" value={post.author_role || ""} onChange={(e) => update("author_role", e.target.value)} className={inputCls} />
            </Field>
          </div>
          <Field label="Tags">
            <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-ridge-border bg-white p-2">
              {(post.tags || []).map((t) => (
                <span key={t} className="inline-flex items-center gap-1 rounded bg-ridge-stone px-2 py-0.5 text-xs font-semibold text-ridge-charcoal">
                  {t}
                  <button type="button" onClick={() => update("tags", post.tags.filter((x) => x !== t))} className="text-ridge-muted hover:text-red-600" aria-label={`Remove ${t}`}>×</button>
                </span>
              ))}
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(); } }}
                placeholder="Type & press Enter"
                className="flex-1 min-w-[120px] bg-transparent px-1 py-1 text-sm focus:outline-none"
                data-testid="admin-blog-tag-input"
              />
            </div>
          </Field>
          <Field label="SEO title">
            <input type="text" value={post.seo_title || ""} onChange={(e) => update("seo_title", e.target.value)} className={inputCls} />
          </Field>
          <Field label="SEO description">
            <textarea value={post.seo_description || ""} onChange={(e) => update("seo_description", e.target.value)} rows={3} maxLength={400} className={inputCls} />
          </Field>
        </aside>
      </div>
    </div>
  );
}

const inputCls =
  "block w-full rounded-md border border-ridge-border bg-white px-3 py-2 text-sm focus:border-ridge-charcoal focus:outline-none focus:ring-2 focus:ring-ridge-charcoal/15";

function Field({ label, hint, required, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.14em] text-ridge-charcoal">
        {label}{required ? <span className="ml-1 text-ridge-amber">*</span> : null}
      </label>
      {children}
      {hint ? <p className="mt-1 text-xs text-ridge-muted">{hint}</p> : null}
    </div>
  );
}
